/**
 * Parsowanie linków / kodu osadzenia Facebook (reel, wideo) i budowa URL playera video.php.
 */

const FB_VIDEO_PAGE_RE =
  /^https?:\/\/(?:www\.|m\.|web\.)?facebook\.com\/(?:reel\/\d+|watch\/?\?v=\d+|[\w.-]+\/videos\/\d+)/i;

/** Proporcje ramki playera FB (jak w kodzie osadzenia: 560×314). */
export function getFacebookEmbedPlayerHeight(widthPx) {
  const w = Math.round(Math.max(280, Math.min(Number(widthPx) || 560, 1080)));
  return Math.round(w * (9 / 16));
}

function hostIsFacebook(hostname) {
  if (!hostname) return false;
  const h = (hostname || '').toLowerCase().replace(/^www\./, '');
  return h === 'facebook.com' || h.endsWith('.facebook.com');
}

function normalizeFromPageUrl(urlStr) {
  try {
    const u = new URL(urlStr);
    if (!hostIsFacebook(u.hostname)) return null;
    const path = u.pathname.replace(/\/+$/, '') || '/';
    if (/\/reel\/\d+$/i.test(path)) {
      return `https://www.facebook.com${path}/`;
    }
    if (/\/videos\/\d+$/i.test(path)) {
      return `https://www.facebook.com${path}/`;
    }
    if (path === '/watch' || path.startsWith('/watch/')) {
      const v = u.searchParams.get('v');
      if (v) return `https://www.facebook.com/watch/?v=${v}`;
    }
    if (FB_VIDEO_PAGE_RE.test(u.href)) return u.href.split('#')[0];
    return null;
  } catch {
    return null;
  }
}

/** Kanoniczny URL strony wideo (reel lub post) do parametru href. */
export function normalizeFacebookVideoPageUrl(input) {
  const raw = (input || '').trim();
  if (!raw) return null;

  const reelInText = raw.match(/facebook\.com\/reel\/(\d+)/i);
  if (reelInText) return `https://www.facebook.com/reel/${reelInText[1]}/`;

  const watchInText = raw.match(/[?&]v=(\d+)/i);
  if (watchInText && /facebook\.com\/watch/i.test(raw)) {
    return `https://www.facebook.com/watch/?v=${watchInText[1]}`;
  }

  const hrefEncoded = raw.match(/[?&]href=([^&"'<>]+)/i);
  if (hrefEncoded) {
    try {
      const decoded = decodeURIComponent(hrefEncoded[1]);
      const fromHref = normalizeFromPageUrl(decoded);
      if (fromHref) return fromHref;
      if (/facebook\.com\/reel\/(\d+)/i.test(decoded)) {
        return normalizeFacebookVideoPageUrl(decoded);
      }
    } catch {
      /* ignore */
    }
  }

  const pluginUrlMatch = raw.match(
    /(https?:\/\/[^\s"'<>]*facebook\.com\/plugins\/video\.php[^\s"'<>]*)/i,
  );
  if (pluginUrlMatch) {
    try {
      const u = new URL(pluginUrlMatch[1]);
      const href = u.searchParams.get('href');
      if (href) {
        const fromHref = normalizeFromPageUrl(decodeURIComponent(href));
        if (fromHref) return fromHref;
      }
    } catch {
      /* ignore */
    }
  }

  const iframeSrc = raw.match(/src\s*=\s*["']([^"']+)["']/i);
  if (iframeSrc && /facebook\.com/i.test(iframeSrc[1])) {
    const fromSrc = normalizeFacebookVideoPageUrl(iframeSrc[1]);
    if (fromSrc) return fromSrc;
  }

  let urlStr = raw;
  if (!/^https?:\/\//i.test(urlStr)) {
    if (/^facebook\.com\//i.test(urlStr)) urlStr = `https://www.${urlStr}`;
    else return null;
  }

  return normalizeFromPageUrl(urlStr);
}

export function buildFacebookVideoEmbedUrl(pageUrl, widthPx = 560) {
  const normalized = normalizeFacebookVideoPageUrl(pageUrl);
  if (!normalized) return null;
  const w = Math.round(Math.max(280, Math.min(Number(widthPx) || 560, 1080)));
  const height = getFacebookEmbedPlayerHeight(w);
  const href = encodeURIComponent(normalized.endsWith('/') ? normalized : `${normalized}/`);
  return `https://www.facebook.com/plugins/video.php?height=${height}&href=${href}&show_text=false&width=${w}&t=0`;
}

export function isFacebookVideoPageUrl(input) {
  return !!normalizeFacebookVideoPageUrl(input);
}

export function blockHasDisplayableContent(block) {
  if (!block) return false;
  if (block.type === 'text') return !!(block.text || block.content || '').trim();
  if (block.type === 'facebookVideo') return !!normalizeFacebookVideoPageUrl(block.url);
  if (block.type === 'image') return !!(block.url || '').trim();
  if (block.type === 'link') return !!(block.url || '').trim();
  return block.type === 'divider';
}
